import { HttpStatusCode, RulebookErrorType, RulebookStatus, RulebookVisibility, } from "@next-platform/packages-ts-types";
import { getErrorType } from "../../../utils/getErrorType";
import { axiosClient, getBaseUrlVersioned } from "../../../services/axiosClient";
import { resolveDraftStorage } from "./rulebookDraftStore";
export const EMPTY_PUBLISHED_RECORD = { live: null, pending: null };
/**
 * The Rulebook changed since the Editor last read it (409), or was never read:
 * saving would overwrite unseen work.
 */
export class RulebookPublishConflictError extends Error {
    /** Set when a repeated first publish found the Rulebook the draft already created (its link had been lost). */
    rulebookId;
    constructor(rulebookId) {
        super("The Rulebook changed since it was loaded");
        this.name = "RulebookPublishConflictError";
        this.rulebookId = rulebookId;
    }
}
/** The Rulebook an update targeted no longer exists (404 on PUT): it was deleted elsewhere since it was loaded. */
export class RulebookPublishMissingError extends Error {
    constructor() {
        super("The Rulebook no longer exists");
        this.name = "RulebookPublishMissingError";
    }
}
/** A write the server refused: carries the error code and message it returned for display. */
export class RulebookPublishRejectedError extends Error {
    errorCode;
    serverMessage;
    /** `flattenRulebookTexts` paths the server sent back with FLAGGED_FIELDS_UNRESOLVED. */
    paths;
    constructor({ errorCode, serverMessage, paths } = {}) {
        super(serverMessage ?? "The Rulebook publish was rejected");
        this.name = "RulebookPublishRejectedError";
        this.errorCode = errorCode;
        this.serverMessage = serverMessage;
        this.paths = paths;
    }
}
const isConflictStatus = (error) => error?.response?.status === HttpStatusCode.CONFLICT;
const isMissingStatus = (error) => error?.response?.status === HttpStatusCode.NOT_FOUND;
/**
 * Rejections worth surfacing: a 4xx the server returned for the write (not auth/not-found, handled
 * elsewhere). A 409 is only a rejection when its error code says so — IN_REVIEW and
 * FLAGGED_FIELDS_UNRESOLVED arrive as 409s; any other 409 is the optimistic lock and reloads.
 */
const isRejectedStatus = (status, code) => status !== undefined &&
    status >= HttpStatusCode.BAD_REQUEST &&
    status < HttpStatusCode.INTERNAL_SERVER_ERROR &&
    status !== HttpStatusCode.UNAUTHORIZED &&
    status !== HttpStatusCode.FORBIDDEN &&
    status !== HttpStatusCode.NOT_FOUND &&
    (status !== HttpStatusCode.CONFLICT || code === RulebookErrorType.IN_REVIEW || code === RulebookErrorType.FLAGGED_FIELDS_UNRESOLVED);
const toRejectedError = (error) => {
    const status = error?.response?.status;
    const body = error?.response?.data;
    if (typeof body !== "object" || body === null) {
        return null;
    }
    const code = typeof body.errorCode === "string" ? getErrorType(body.errorCode, RulebookErrorType) : null;
    if (!isRejectedStatus(status, code)) {
        return null;
    }
    // `id-denylist` bans a `data` identifier; the wire field keeps its name via bracket access.
    const bodyData = body["data"];
    const paths = bodyData?.paths;
    return new RulebookPublishRejectedError({
        ...(typeof body.errorCode === "string" ? { errorCode: body.errorCode } : {}),
        ...(typeof body.message === "string" ? { serverMessage: body.message } : {}),
        ...(Array.isArray(paths) ? { paths: paths.filter((path) => typeof path === "string") } : {}),
    });
};
const rulebooksUrl = (path = "") => `${getBaseUrlVersioned(null)}/rulebooks${path}`;
/** The create/update body: only the fields the endpoints accept (they reject unknown keys, e.g. the draft's local id). */
const toWriteBody = ({ definition, meta }) => ({
    name: definition.name,
    ...(definition.description !== undefined && { description: definition.description }),
    meters: definition.meters,
    combined_rules: definition.combined_rules,
    ...(meta.tags !== undefined && { tags: meta.tags }),
    ...(meta.cover_url !== undefined && { cover_url: meta.cover_url }),
    visibility: meta.visibility ?? RulebookVisibility.PRIVATE,
    ...(meta.definition_visible !== undefined && { definition_visible: meta.definition_visible }),
});
const toPendingVersion = (live, submission) => ({
    version: submission.version,
    definition: {
        id: live.id,
        name: submission.name,
        ...(submission.description !== undefined && { description: submission.description }),
        meters: submission.meters,
        combined_rules: submission.combined_rules,
    },
    meta: {
        ...(submission.cover_url !== undefined && { cover_url: submission.cover_url }),
        tags: submission.tags,
        visibility: RulebookVisibility.PUBLIC,
        definition_visible: submission.definition_visible,
    },
    submitted_at: new Date(submission.submitted_at ?? Date.now()).toISOString(),
    moderation_status: submission.moderation_status,
    ...(submission.reasons !== undefined && { reasons: submission.reasons }),
    ...(submission.note !== undefined && { note: submission.note }),
    ...(submission.flagged_fields !== undefined && { flagged_fields: submission.flagged_fields }),
    ...(submission.flagged_cover !== undefined && { flagged_cover: submission.flagged_cover }),
});
const toRecord = ({ pending_submission: submission, ...live }) => ({
    live,
    pending: submission ? toPendingVersion(live, submission) : null,
});
/** The Editor's publishing client, backed by the Rulebook CRUD endpoints. */
export const apiRulebookPublishingClient = {
    getRulebook: async ({ rulebookId }) => {
        try {
            const response = await axiosClient.get(rulebooksUrl(`/${encodeURIComponent(rulebookId)}`));
            return toRecord(response.data);
        }
        catch (error) {
            if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
                return null;
            }
            throw error;
        }
    },
    publishRulebook: async (request) => {
        const body = toWriteBody(request);
        const write = request.rulebookId
            ? axiosClient.put(rulebooksUrl(`/${encodeURIComponent(request.rulebookId)}`), {
                ...body,
                ...(request.expectedUpdatedAt !== undefined && { expected_updated_at: request.expectedUpdatedAt }),
            })
            : axiosClient.post(rulebooksUrl(), {
                ...body,
                ...(request.publishKey !== undefined && { client_publish_key: request.publishKey }),
            });
        const response = await write.catch((error) => {
            // A 404 only matters on update: the linked Rulebook is gone. On create it is a generic failure.
            if (isMissingStatus(error) && request.rulebookId !== undefined) {
                throw new RulebookPublishMissingError();
            }
            const rejection = toRejectedError(error);
            if (rejection) {
                throw rejection;
            }
            if (isConflictStatus(error)) {
                throw new RulebookPublishConflictError();
            }
            throw error;
        });
        const live = response.data.rulebook;
        if (response.data.replayed) {
            // The draft had already created this Rulebook; nothing from this request was saved. Relink and review it.
            throw new RulebookPublishConflictError(live.id);
        }
        if (!response.data.pending) {
            return { live, pending: null };
        }
        // A public submission leaves the live row as it was; what was sent waits for review as the next version.
        const pending = {
            version: live.version + 1,
            definition: { ...request.definition, id: live.id },
            meta: { ...request.meta, visibility: RulebookVisibility.PUBLIC },
            submitted_at: new Date().toISOString(),
        };
        return { live, pending };
    },
    deleteRulebook: async ({ rulebookId }) => {
        try {
            await axiosClient.delete(rulebooksUrl(`/${encodeURIComponent(rulebookId)}`));
        }
        catch (error) {
            if (error?.response?.status !== HttpStatusCode.NOT_FOUND) {
                throw error;
            }
        }
    },
};
const MOCK_STORAGE_KEY = "rulebook.editor.mockPublished.v1";
const MOCK_LATENCY_MS = 350;
const MOCK_CREATOR_ID = "mock-creator";
const readStore = (storage) => {
    try {
        const raw = storage.getItem(MOCK_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return typeof parsed === "object" && parsed !== null ? parsed : {};
    }
    catch {
        return {};
    }
};
const writeStore = (storage, store) => {
    storage.setItem(MOCK_STORAGE_KEY, JSON.stringify(store));
};
const delay = (latencyMs) => new Promise((resolve) => setTimeout(resolve, latencyMs));
const toLiveRulebook = ({ definition, meta, version }) => ({
    ...definition,
    ...(meta.cover_url !== undefined ? { cover_url: meta.cover_url } : {}),
    tags: meta.tags ?? [],
    creator_user_id: MOCK_CREATOR_ID,
    application_ids: [],
    visibility: meta.visibility ?? RulebookVisibility.PRIVATE,
    definition_visible: meta.definition_visible ?? false,
    status: RulebookStatus.ACTIVE,
    version,
    updatedAt: Date.now(),
});
/**
 * Publishing rules the backend enforces: private saves go live under the current version (v1 when new), public saves
 * wait for review as the next one. A first save always creates the live row as a private v1, so a first public save
 * leaves v1 live and stages v2.
 */
export const applyPublish = (record, request, now) => {
    const liveVersion = record.live?.version ?? 0;
    if (request.meta.visibility === RulebookVisibility.PUBLIC) {
        const live = record.live ?? toLiveRulebook({ ...request, meta: { ...request.meta, visibility: RulebookVisibility.PRIVATE }, version: 1 });
        const pending = { version: live.version + 1, definition: request.definition, meta: request.meta, submitted_at: now.toISOString() };
        return { live, pending };
    }
    return { live: toLiveRulebook({ ...request, version: Math.max(liveVersion, 1) }), pending: null };
};
/**
 * In-memory stand-in with the same shapes and publishing rules, for tests and the local harness (no backend).
 * Rulebooks are keyed by the draft id, which doubles as their "server" id.
 */
export const createMockRulebookPublishingClient = ({ storage: fixedStorage, latencyMs = MOCK_LATENCY_MS, } = {}) => {
    // Resolved per call, so it follows the draft store's current owner.
    const storageNow = () => fixedStorage ?? resolveDraftStorage();
    return {
        getRulebook: async ({ rulebookId }) => {
            await delay(latencyMs);
            return readStore(storageNow())[rulebookId] ?? null;
        },
        publishRulebook: async (request) => {
            await delay(latencyMs);
            const storage = storageNow();
            const store = readStore(storage);
            const key = request.rulebookId ?? request.definition.id;
            const next = applyPublish(store[key] ?? EMPTY_PUBLISHED_RECORD, request, new Date());
            writeStore(storage, { ...store, [key]: next });
            return next;
        },
        deleteRulebook: async ({ rulebookId }) => {
            await delay(latencyMs);
            const storage = storageNow();
            const store = readStore(storage);
            delete store[rulebookId];
            writeStore(storage, store);
        },
    };
};
